import { useState } from 'react';
import { playBeep } from '../../../../utils/audio';
import { validateEanInputs, validatePrinter } from '../../../../utils/printValidation';

export function useOrderPrint({
  selectedOrder,
  printType,
  eanValidated,
  selectedPrinter,
  labelFormat,
  bridgeConnected,
  printLabel,
  setPrinterModalOpen,
  refetchLabel,
  onPrintSuccess,
  labelData,
  labelLoading,
  labelError: labelApiError,
  invoiceData,
}) {
  const [isPrinting, setIsPrinting] = useState(false);
  const [showForceDialog, setShowForceDialog] = useState(false);
  const [labelError, setLabelError] = useState(null);

  // Função principal de impressão
  const handlePrintLabel = async (retry = false, skipForceCheck = false) => {
    if (isPrinting) return;
    
    // Se houver erro anterior, tenta refazer as requisições antes de continuar
    if ((labelApiError || labelError) && refetchLabel) {
      try {
        setIsPrinting(true);
        setLabelError(null);
        // Executa o refetch e espera completar
        await refetchLabel();
        
        // Espera um momento para garantir que os dados foram atualizados
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Retorna para permitir que os novos dados sejam processados
        setIsPrinting(false);
        return handlePrintLabel(false, skipForceCheck);
      } catch (error) {
        setLabelError('Erro ao buscar etiqueta novamente: ' + (error.message || error));
        playBeep(200, 0.3, 'error');
        setIsPrinting(false);
        return;
      }
    }
    
    // Validação de impressora
    if (!validatePrinter(selectedPrinter, labelFormat)) {
      setPrinterModalOpen(true);
      playBeep(200, 0.3, 'error');
      return;
    }
    // Validação de EANs (detalhada)
    let missingGtin = false;
    let missingValidation = false;
    if (
      printType === 'detalhada' &&
      selectedOrder &&
      invoiceData &&
      invoiceData.shipping &&
      Array.isArray(invoiceData.shipping.items_gtin)
    ) {
      const result = validateEanInputs(invoiceData.shipping.items_gtin, eanValidated);
      missingGtin = result.missingGtin;
      missingValidation = result.missingValidation;
    }
    if (
      printType === 'detalhada' &&
      selectedOrder &&
      invoiceData &&
      invoiceData.shipping &&
      Array.isArray(invoiceData.shipping.items_gtin) &&
      (missingGtin || missingValidation) &&
      !skipForceCheck &&
      !showForceDialog
    ) {
      setShowForceDialog(true);
      setLabelError('Preencha corretamente todos os códigos EAN para imprimir detalhada.');
      playBeep(200, 0.3, 'error');
      return;
    }
    setIsPrinting(true);
    setLabelError(null);
    // Checagem de bridge/printer
    if (!bridgeConnected && labelFormat === 'zpl') {
      setLabelError('Bridge não conectado. Instale e execute o Wala Print Bridge.');
      playBeep(200, 0.3, 'error');
      setIsPrinting(false);
      return;
    }
    try {
      // Se ainda estiver carregando, aguarda
      if (labelLoading) {
        throw new Error('Aguarde, carregando etiqueta...');
      }

      // Se não temos dados e não está carregando, tenta refazer a requisição
      if (!labelData && labelFormat === 'zpl' && refetchLabel) {
        await refetchLabel();
        // Aguarda um momento para os dados serem atualizados
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Verifica novamente se temos os dados após o refetch
      if (!labelData && labelFormat === 'zpl') {
        throw new Error('Não foi possível obter os dados da etiqueta. Tente novamente.');
      }
      
      // Usa dados da API se disponíveis, senão usa mock
      const zplData = labelData || selectedOrder?.zplData || '^XA^FO50,50^ADN,36,20^FDImpressão de Teste^FS^XZ';
      
      if (labelFormat === 'zpl') {
        await printLabel(zplData);
        playBeep(1200, 0.2, 'success');
      } else {
        const blob = new Blob(['PDF de teste'], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        playBeep(1200, 0.2, 'success');
      }
      setLabelError(null);
      setShowForceDialog(false);
      
      // Chama callback de sucesso apenas se não houver erro
      if (onPrintSuccess) {
        onPrintSuccess();
      }
    } catch (error) {
      setLabelError('Erro na impressão: ' + (error.message || error));
      playBeep(200, 0.3, 'error');
      // Não chama callback de sucesso em caso de erro
    } finally {
      setIsPrinting(false);
    }
  };

  // Modal de confirmação para forçar impressão
  const handleCloseForceDialog = () => {
    setShowForceDialog(false);
  };
  const handleConfirmForceDialog = () => {
    setShowForceDialog(false);
    setTimeout(() => handlePrintLabel(false, true), 100);
  };

  return {
    isPrinting,
    showForceDialog,
    labelError,
    handlePrintLabel,
    handleCloseForceDialog,
    handleConfirmForceDialog,
    setShowForceDialog,
    setLabelError,
  };
} 